import { useEffect, useState } from 'react';
import type { Key } from 'react';
import { useTranslate } from '@refinedev/core';
import { BooleanField, useTable } from '@refinedev/antd';
import { Card, Flex, Table, Typography, theme } from 'antd';
import type {
  ConsentOut,
  UCConsentDetailOut,
  UCConsentOut,
} from '@/api/generated/schemas';
import { orDash } from '@/components/common/show/shared';
import { ConsentKindTag } from '@/components/iam/tags';
import { formatDateTime } from '@/utils/format';

const { Text } = Typography;

const SystemConsentsCard = ({ userId }: { userId: number }) => {
  const t = useTranslate();
  const { tableProps, result } = useTable<ConsentOut>({
    resource: 'iam.consents',
    filters: {
      permanent: [{ field: 'user_id', operator: 'eq', value: userId }],
    },
    pagination: { pageSize: 10 },
    syncWithLocation: false,
  });

  return (
    <Card
      title={`${t('iam.users.consents.system_section')} (${result?.total ?? 0})`}
    >
      <Table<ConsentOut>
        {...tableProps}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
        size="small"
      >
        <Table.Column<ConsentOut>
          dataIndex="kind"
          title={t('iam.users.consents.fields.kind')}
          render={(value: string) => <ConsentKindTag kind={value} />}
        />
        <Table.Column<ConsentOut>
          dataIndex="is_accepted"
          title={t('iam.users.consents.fields.is_accepted')}
          render={(value: boolean) => <BooleanField value={value} />}
        />
        <Table.Column<ConsentOut>
          dataIndex="version"
          title={t('iam.users.consents.fields.version')}
          render={(value: string | null) => orDash(value)}
        />
        <Table.Column<ConsentOut>
          dataIndex="accepted_at"
          title={t('iam.users.consents.fields.accepted_at')}
          render={(value: string | null) => orDash(formatDateTime(value))}
        />
        <Table.Column<ConsentOut>
          dataIndex="updated_at"
          title={t('base.fields.updated_at')}
          render={(value: string | null) => orDash(formatDateTime(value))}
        />
      </Table>
    </Card>
  );
};

const UCConsentsCard = ({ userId }: { userId: number }) => {
  const t = useTranslate();
  const { tableProps, result } = useTable<UCConsentOut>({
    resource: 'iam.uc_consents',
    filters: {
      permanent: [{ field: 'user_id', operator: 'eq', value: userId }],
    },
    pagination: { pageSize: 10 },
    syncWithLocation: false,
  });

  // Rows with detailed consents start expanded so the breakdown is visible.
  const [expandedRowKeys, setExpandedRowKeys] = useState<readonly Key[]>([]);
  useEffect(() => {
    setExpandedRowKeys(
      (tableProps.dataSource ?? [])
        .filter((r) => r.detailed_consents.length > 0)
        .map((r) => r.id)
    );
  }, [tableProps.dataSource]);

  return (
    <Card
      title={`${t('iam.users.consents.uc_section')} (${result?.total ?? 0})`}
    >
      <Table<UCConsentOut>
        {...tableProps}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
        size="small"
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: setExpandedRowKeys,
          rowExpandable: (row) => row.detailed_consents.length > 0,
          expandedRowRender: (row) => (
            <Table<UCConsentDetailOut>
              dataSource={row.detailed_consents}
              rowKey="id"
              size="small"
              pagination={false}
            >
              <Table.Column<UCConsentDetailOut>
                dataIndex="id"
                title={t('base.fields.id')}
                render={(value: string) => <Text code>{value}</Text>}
              />
              <Table.Column<UCConsentDetailOut>
                dataIndex="provider"
                title={t('iam.users.consents.fields.provider')}
                render={(value: string | null) => orDash(value)}
              />
              <Table.Column<UCConsentDetailOut>
                dataIndex="is_accepted"
                title={t('iam.users.consents.fields.is_accepted')}
                render={(value: boolean) => <BooleanField value={value} />}
              />
            </Table>
          ),
        }}
      >
        <Table.Column<UCConsentOut>
          dataIndex="controller_id"
          title={t('iam.users.consents.fields.controller_id')}
          render={(value: string) => <Text code>{value}</Text>}
        />
        <Table.Column<UCConsentOut>
          dataIndex="detailed_consents"
          title={t('iam.users.consents.fields.detailed_consents')}
          render={(value: UCConsentDetailOut[]) => value.length}
        />
        <Table.Column<UCConsentOut>
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          render={(value: string | null) => orDash(formatDateTime(value))}
        />
        <Table.Column<UCConsentOut>
          dataIndex="updated_at"
          title={t('base.fields.updated_at')}
          render={(value: string | null) => orDash(formatDateTime(value))}
        />
      </Table>
    </Card>
  );
};

export const UserConsentsTab = ({ userId }: { userId: number }) => {
  const { token } = theme.useToken();
  return (
    <Flex vertical gap={token.margin}>
      <SystemConsentsCard userId={userId} />
      <UCConsentsCard userId={userId} />
    </Flex>
  );
};
